import { type ClassValue } from "clsx";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { WooOrder } from "@/types/order";
import { Clock, Package, CheckCircle2, XCircle, AlertCircle, RefreshCcw, Ban } from "lucide-react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getStatusBadge(status: WooOrder['status']) {
  const statusConfig = {
    'pending': { color: "bg-yellow-100 text-yellow-800", icon: Clock },
    'processing': { color: "bg-blue-100 text-blue-800", icon: Package },
    'completed': { color: "bg-green-100 text-green-800", icon: CheckCircle2 },
    'cancelled': { color: "bg-red-100 text-red-800", icon: XCircle },
    'on-hold': { color: "bg-orange-100 text-orange-800", icon: AlertCircle },
    'refunded': { color: "bg-purple-100 text-purple-800", icon: RefreshCcw },
    'failed': { color: "bg-red-100 text-red-800", icon: Ban }
  } as const;

  return statusConfig[status] || statusConfig['pending'];
}

interface SortConfig {
  key: keyof WooOrder | 'customerName';
  direction: 'asc' | 'desc';
}

export function sortOrders(orders: WooOrder[], sortConfig: SortConfig) {
  return [...orders].sort((a, b) => {
    if (sortConfig.key === 'customerName') {
      const nameA = `${a.billing.first_name} ${a.billing.last_name}`.toLowerCase();
      const nameB = `${b.billing.first_name} ${b.billing.last_name}`.toLowerCase();
      return sortConfig.direction === 'asc' 
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    }
    
    if (sortConfig.key === 'total') {
      const totalA = parseFloat(a[sortConfig.key]);
      const totalB = parseFloat(b[sortConfig.key]);
      return sortConfig.direction === 'asc' 
        ? totalA - totalB 
        : totalB - totalA;
    }

    if (sortConfig.key === 'date_created' || sortConfig.key === 'date_modified') {
      const dateA = new Date(a[sortConfig.key]).getTime();
      const dateB = new Date(b[sortConfig.key]).getTime();
      return sortConfig.direction === 'asc'
        ? dateA - dateB
        : dateB - dateA;
    }

    const valueA = a[sortConfig.key];
    const valueB = b[sortConfig.key];
    return sortConfig.direction === 'asc'
      ? String(valueA).localeCompare(String(valueB))
      : String(valueB).localeCompare(String(valueA));
  });
}

export function formatPrice(price: string | number) {
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(numericPrice);
}

export function formatDate(date: any) {
  // Gestione del timestamp Firestore
  if (date && date._seconds) {
    // Converti i secondi in millisecondi
    const milliseconds = date._seconds * 1000;
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(milliseconds));
  }

  // Fallback per altri formati di data
  try {
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(date));
  } catch (error) {
    console.error('Errore nella formattazione della data:', error);
    return 'Data non valida';
  }
}

export const translateAction = (action: string): string => {
  const actionMap: { [key: string]: string } = {
    'start_packaging': 'Preso in carico',
    'update_packed_item': 'Modifica imballaggio',
    // Aggiungi qui altre azioni quando necessario
  };

  return actionMap[action] || action; // Fallback all'azione originale se non trovata
};

export function calculateTotalWeight(order: any): number {
  if (!order?.line_items?.length) return 0;

  return order.line_items.reduce((totalWeight: number, item: any) => {
    try {
      const quantityMeta = item.meta_data?.find(
          (meta: any) => meta.key === 'pa_quantity'
      );

      // Caso senza metadato o display_value: applica peso basato sul tipo di prodotto
      if (!quantityMeta?.display_value) {
        // Controlla se è un box ortaggi dal nome del prodotto
        const isBoxOrtaggi = item.name && item.name.toLowerCase().includes('box') && item.name.toLowerCase().includes('ortaggi');
        
        if (isBoxOrtaggi) {
          // Box ortaggi: peso 5 kg per unità
          return totalWeight + 5 * (item.quantity || 1);
        } else {
          // Altri prodotti senza metadato peso (es. set olio): peso default 0.5 kg
          return totalWeight + 0.5 * (item.quantity || 1);
        }
      }

      // Estrazione valore e unità con regex migliorata
      const weightMatch = quantityMeta.display_value.match(/(\d+[,.]?\d*)\s*(kg|g)?/i);

      if (!weightMatch) {
        return totalWeight; // Formato non riconosciuto
      }

      const valueStr = weightMatch[1].replace(',', '.');
      const value = parseFloat(valueStr);

      if (isNaN(value)) {
        return totalWeight; // Valore non numerico
      }

      // Determina unità di misura
      const unit = (weightMatch[2] || 'kg').toLowerCase();
      const weightInKg = unit === 'g' ? value / 1000 : value;

      return totalWeight + (weightInKg * (item.quantity || 1));
    } catch (error) {
      console.error(`Errore nel calcolo del peso per l'item:`, item);
      return totalWeight;
    }
  }, 0);
}

// Calcola il numero di colli basato sul peso (dividendo per 9 e arrotondando a numeri interi)
export function calculatePackages(weight: number): number {
  if (weight <= 0) return 0;
  
  // Anche per pesi minimi (< 9kg), deve esserci almeno 1 collo
  if (weight < 9) return 1;
  
  // Dividi il peso per 9 per ottenere il numero grezzo di colli
  const rawPackages = weight / 9;
  
  // Arrotonda in base alle regole specificate:
  // - Se il decimale è <= 0.5, arrotonda a 1
  // - Se il decimale è > 0.5, arrotonda a 2
  const decimalPart = rawPackages - Math.floor(rawPackages);
  
  if (decimalPart <= 0.5) {
    return Math.floor(rawPackages);
  } else {
    return Math.ceil(rawPackages);
  }
}
